import { isRuleGroup, RuleGroupTypeAny } from 'react-querybuilder'
import type { CustomField } from './types'

export const lookupPipeline = (
  from: string,
  path: string,
  foreignField: string = '_id',
  as: string = path
): Array<Record<string, any>> => {
  return [
    {
      $lookup: {
        from,
        localField: path,
        foreignField,
        as
      }
    },
    {
      $unwind: {
        path: `$${as}`,
        preserveNullAndEmptyArrays: true
      }
    }
  ]
}

/**
 * Deduplicates pipeline stages by reference, preserving order.
 * Stages added by lookupPipeline are defined at module level as constants,
 * so reference equality correctly identifies duplicates across fields
 * that share the same lookup (e.g. contract.name and contract.abbreviation).
 */
const dedupe = (stages: Array<Record<string, any>>): Array<Record<string, any>> => {
  return stages.filter((v, i) => stages.indexOf(v) === i)
}

/**
 * Recursively collects pipeline stages from fields referenced in a query rule group.
 * Walks nested rule groups to find all field names used in filter conditions,
 * then returns the lookup pipeline stages for those fields.
 */
const getPipelineFromQuery = (group: RuleGroupTypeAny, fields: CustomField[]): Array<Record<string, any>> => {
  if (!isRuleGroup(group)) return []
  return group.rules.flatMap(r => {
    if (isRuleGroup(r)) return getPipelineFromQuery(r, fields)
    const fieldName = typeof r === 'string' ? r : r.field
    return fields.find(f => f.name === fieldName)?.pipeline ?? []
  })
}

/**
 * Returns the pipeline stages needed to resolve a list of selected display fields.
 * This ensures lookup joins are included even when a field is selected for display
 * but not used as a filter condition in the query.
 */
const getPipelineFromSelectedFields = (selectedFields: CustomField[]): Array<Record<string, any>> => {
  return selectedFields.flatMap(f => f.pipeline ?? [])
}

/**
 * Builds the full aggregation pipeline for a report by combining stages required
 * by the query filter conditions and stages required by the selected display fields.
 * Duplicate stages (e.g. the same lookup needed by both a filter and a display field)
 * are removed so each join only runs once.
 */
export const createPipeline = (
  group: RuleGroupTypeAny,
  allFields: CustomField[],
  selectedFields: CustomField[] = []
): Array<Record<string, any>> => {
  const fromQuery = getPipelineFromQuery(group, allFields)
  const fromSelected = getPipelineFromSelectedFields(selectedFields)
  return dedupe([...fromQuery, ...fromSelected])
}
